import {Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild,} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {EsvgFiles, SvgService} from 'frontier/nucleus';
import {
  IUploadDocument,
  IUploadRejection,
  TUploadRejectionReason
} from 'frontier/browserkit/src/lib/components/upload-wrapper/uploaded-document.interface';
import {MatIconButton} from '@angular/material/button';
import {MatProgressBar} from '@angular/material/progress-bar';
import {NgIf} from '@angular/common';
import {MatChipListbox, MatChipOption} from '@angular/material/chips';
import {MatIcon} from '@angular/material/icon';
import {DropUploadDirective} from './drop-upload.directive';
import {MatSnackBar} from "@angular/material/snack-bar";
import {
  acceptIsFullySniffable,
  describeAccept,
  detectFileSignature,
  IFileSignature,
  matchesAccept,
  parseAccept
} from './file-signature';

@Component({
  selector: 'app-upload-wrapper',
  templateUrl: './upload-wrapper.component.html',
  styleUrls: ['./upload-wrapper.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UploadWrapperComponent,
    },
  ],
  standalone: true,
  imports: [
    DropUploadDirective,
    MatIcon,
    MatChipListbox,
    MatChipOption,
    NgIf,
    MatProgressBar,
    MatIconButton,
  ],
})
export class UploadWrapperComponent implements ControlValueAccessor {
  private _snackbar: MatSnackBar = inject(MatSnackBar);

  @ViewChild('fileUpload') fileUploadRef: ElementRef;

  @Input() readAs: 'ArrayBuffer' | 'Text' | 'Image' = 'ArrayBuffer';
  @Input() accept: string;

  @Input() maxSize: number;  // in Byte
  @Input() uploading = false;
  @Output() changed = new EventEmitter<IUploadDocument>();
  /** emitted instead of `changed` when a file fails the accept/maxSize checks */
  @Output() rejected = new EventEmitter<IUploadRejection>();

  document: IUploadDocument =
    null;
  private touched: boolean;

  onChange: (document: IUploadDocument) => void;
  onTouched: () => void;

  constructor(private svgService: SvgService) {
  }

  get fileName(): string {
    if (this.document && this.document.name) {
      const array = this.document.name.split('\\');
      return array[array.length - 1];
    }
    return null;
  }

  onUploadClick() {
    this.fileUploadRef.nativeElement.click();
  }

  private readFile(fileRead: File, detected: IFileSignature | null): void {
    const reader: FileReader = new FileReader();
    reader.onloadend = (e: ProgressEvent<FileReader>) => {
      // Ergebnis vom FileReader auslesen
      this.document = {
        // give the file the extension its content actually calls for, so the
        // backend can key its converter off the name it receives
        name: detected ? this.withExtension(fileRead.name, detected.ext) : fileRead.name,
        data: e.target.result,
        fileFormate: (detected?.mime ?? fileRead.type).split('/').pop(),
        detectedType: detected?.mime ?? null,
      };
      if (this.onChange) {
        this.onChange(this.document);
      }
      this.changed.emit(this.document); // Emit the change event
      this.markAsTouched();
    };

    switch (this.readAs) {
      case 'Image':
        reader.readAsDataURL(fileRead);
        break;
      case 'ArrayBuffer':
        reader.readAsArrayBuffer(fileRead);
        break;
      case 'Text':
        reader.readAsText(fileRead, 'UTF-8');
        break;
    }
  }

  onFileDrop(evt: FileList) {
    if (!evt || evt.length === 0) {
      return;
    }
    const fileRead: File = evt[0];

    const dT: DataTransfer = new DataTransfer();
    dT.items.add(fileRead);
    this.fileUploadRef.nativeElement.files = dT.files;

    void this.handleFile(fileRead);
  }

  /** Replaces the trailing extension, or appends one when there is none. */
  private withExtension(name: string, ext: string): string {
    const base: string = name.replace(/\.[^.\\/]*$/, '');
    return (base || name) + ext;
  }

  /**
   * Single entry point for both the file dialog and drag & drop, so `accept`
   * and `maxSize` apply to dropped files too.
   */
  private async handleFile(fileRead: File): Promise<void> {
    if (!fileRead) {
      return;
    }

    if (this.maxSize && fileRead.size > this.maxSize) {
      this.reject('size', fileRead, null,
        'Diese Datei ist zu groß. Ihre Datei darf eine Größe von '
        + Intl.NumberFormat('de-DE').format(this.maxSize) + ' Byte nicht überschreiten.');
      return;
    }

    let detected: IFileSignature | null = null;
    try {
      detected = await detectFileSignature(fileRead);
    } catch {
      // head unreadable - fall back to the filename based check below
      detected = null;
    }

    if (this.accept) {
      const tokens: string[] = parseAccept(this.accept);
      const allowed: string = describeAccept(tokens);
      const suffix: string = allowed ? ` Erlaubt sind: ${allowed}.` : '';

      if (detected) {
        if (!matchesAccept(tokens, detected.mime, detected.ext)) {
          this.reject('format', fileRead, detected.mime,
            `Dateien vom Typ ${detected.label} werden nicht unterstützt.` + suffix);
          return;
        }
      } else if (acceptIsFullySniffable(tokens)) {
        // every accepted type has a recognisable signature, so unrecognised
        // content cannot be one of them - no matter what the file is named
        this.reject('unknownFormat', fileRead, null,
          'Das Format dieser Datei konnte nicht erkannt werden.' + suffix);
        return;
      } else if (!this.isFileTypeAccepted(fileRead)) {
        // accept covers types we cannot sniff (e.g. text) - fall back to the
        // browser supplied type
        this.reject('format', fileRead, null,
          'Dieses Dateiformat wird nicht unterstützt.' + suffix);
        return;
      }
    }

    this.readFile(fileRead, detected);
  }

  private reject(
    reason: TUploadRejectionReason,
    file: File,
    detectedType: string | null,
    message: string
  ): void {
    // clear the native input so picking the same file again re-fires `change`
    if (this.fileUploadRef?.nativeElement) {
      this.fileUploadRef.nativeElement.value = '';
    }
    this._snackbar.open(message, 'ok', {
      verticalPosition: 'top',
      duration: 10000,
    });
    this.rejected.emit({reason, file, detectedType, message});
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  markAsTouched() {
    if (!this.touched) {
      if (this.onTouched) {
        this.onTouched();
      }
      this.touched = true;
    }
  }

  writeValue(obj: any): void {
    this.document = obj;
  }

  addAttachment(evt: Event): void {
    void this.handleFile((evt.target as HTMLInputElement).files?.[0]);
  }

  private isFileTypeAccepted(file: File): boolean {
    if (!this.accept) {
      return true;
    }

    const acceptTypes: string[] =
      this.accept.split(',').map((type: string): string => type.trim().toLowerCase());
    const fileName: string = file.name.toLowerCase();
    const fileType: string = file.type.toLowerCase();

    return acceptTypes.some((accept: string): boolean => {
      if (accept.startsWith('.')) {
        return fileName.endsWith(accept);
      }
      if (accept.endsWith('/*')) {
        const baseType: string = accept.replace('/*', '');
        return fileType.startsWith(baseType);
      }
      return fileType === accept;
    });
  }

  onDeleteImage(): void {
    this.document = null;
    if (this.onChange) {
      this.onChange(this.document);
    }
    this.fileUploadRef.nativeElement.value = '';
    this.uploading = false;
    if (this.document) {
      this.changed.emit(this.document); // Emit the change event
    }
  }

  protected readonly EsvgFiles = EsvgFiles;
}
